// ReviewsTestimonials.js
import React from 'react';
import { Star, Phone, Quote } from 'lucide-react';

const ReviewsTestimonials = () => {
  const testimonials = [
    {
      name: "Margaret T.",
      location: "Coolamon",
      rating: 5,
      text: "The care my mother receives from Allawah has been nothing short of exceptional. Her caregiver, Sarah, is patient, kind, and treats Mum with such dignity. We finally have peace of mind knowing she's in good hands.",
      service: "Personal Care"
    },
    {
      name: "David & Helen R.",
      location: "Wagga Wagga",
      rating: 5,
      text: "After Dad's stroke, we needed specialised care quickly. Allawah's team was responsive, professional, and matched us with a caregiver who understood his needs perfectly. The progress he's made has been remarkable.",
      service: "Post-Operative Care"
    },
    {
      name: "Jennifer M.",
      location: "Coolamon",
      rating: 5,
      text: "As a full-time caregiver for my husband with Alzheimer's, I was exhausted. The respite care from Allawah gave me the break I desperately needed. I came back refreshed, and my husband was happy and well-cared for.",
      service: "Respite Care"
    },
    {
      name: "Robert K.",
      location: "Wagga Wagga",
      rating: 5,
      text: "I was hesitant about having someone come into my home, but the companionship care has changed my life. My caregiver doesn't just help with daily tasks—she's become a friend. I look forward to her visits every week.",
      service: "Companionship Care"
    },
    {
      name: "Patricia L.",
      location: "Coolamon",
      rating: 5,
      text: "When Mum was diagnosed with dementia, we didn't know where to turn. Allawah's team guided us through every step, and their specialised dementia care has been a blessing. Mum is comfortable, safe, and still living at home.",
      service: "Alzheimer's & Dementia Care"
    },
    {
      name: "Michael S.",
      location: "Wagga Wagga",
      rating: 5,
      text: "The hospice care provided to my father in his final weeks was compassionate and dignified. The caregiver supported not just Dad, but our entire family. We're forever grateful for their kindness during such a difficult time.",
      service: "Hospice Care"
    }
  ];

  const stats = [
    { number: "500+", label: "Families Served" },
    { number: "98%", label: "Client Satisfaction" },
    { number: "15+", label: "Years Combined Experience" },
    { number: "24/7", label: "Care Available" }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gray-50 py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
            Reviews & Testimonials
          </h1>
          <p className="text-lg text-gray-700 font-light mb-8">
            Hear from the families we've had the privilege to serve. Their stories reflect our commitment to providing compassionate, quality care that makes a real difference in people's lives.
          </p>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#E85D2C] mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-700 font-light">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            What Families Are Saying
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-8 rounded-lg shadow-sm relative">
                <Quote size={40} className="text-[#E85D2C] opacity-20 absolute top-4 left-4" />
                <div className="relative z-10">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={20} className="fill-[#E85D2C] text-[#E85D2C]" />
                    ))}
                  </div>
                  <p className="text-gray-700 font-light leading-relaxed mb-6 italic">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t border-gray-200 pt-4">
                    <div className="font-semibold text-gray-900">
                      {testimonial.name}
                    </div>
                    <div className="text-sm text-gray-600">
                      {testimonial.location}
                    </div>
                    <div className="text-sm text-[#E85D2C] mt-1">
                      {testimonial.service}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Your Trust Is Our Priority
          </h2>
          <p className="text-lg text-gray-700 font-light leading-relaxed mb-8">
            These testimonials represent just a few of the many families we've been honoured to serve. Every review reminds us why we do what we do—to provide care that truly makes a difference. We're committed to earning your trust through exceptional service, compassionate caregivers, and unwavering dedication to your loved one's well-being.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-[#AD2647] to-[#E85D2C] py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Experience the Allawah Difference
          </h2>
          <p className="text-lg text-white font-light mb-8">
            Join the families who trust us with their loved ones' care. Contact us today to learn more.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+61269272706"
              className="inline-flex items-center justify-center gap-2 bg-white hover:bg-gray-100 text-[#E85D2C] font-medium px-6 py-3 rounded-full transition-colors"
            >
              <Phone size={20} />
              CALL: (02) 6927 2706
            </a>
            <a 
              href="/contact"
              className="inline-flex items-center justify-center gap-2 bg-transparent hover:bg-white/10 text-white font-medium px-6 py-3 rounded-full border-2 border-white transition-colors"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsTestimonials;
